import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'react-toastify';
import { Plus } from 'lucide-react';
import { webhookService, inboundWebhookService } from '../api';
import { Layout } from '../components/Layout';
import { Button, ThemedText } from '../components';
import { PageContainer, PageHeaderRow } from '../components/PageStyles';
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from '../components/TableStyles';
import { ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter } from '../components/ModalStyles';
import { StatusBadge } from '../components/Badge';
import { FormGroup, FormLabel, StyledSelect } from '../components/FormStyles';

// ─── Styled Components ──────────────────────────────────────────

const TabRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Tab = styled.button`
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  background: transparent;
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  font-weight: ${({ $active }) => $active ? 600 : 400};
  cursor: pointer;
  font-size: 0.9rem;
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;


const EventBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${({ theme }) => theme.colors.primary}15;
  color: ${({ theme }) => theme.colors.primary};
  margin-right: 4px;
  margin-bottom: 2px;
`;

const SecretDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const SecretCode = styled.code`
  flex: 1;
  word-break: break-all;
  font-size: 0.85rem;
`;

const DisabledBar = styled.div`
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  background: ${({ theme }) => theme.colors.warning}15;
  border: 1px solid ${({ theme }) => theme.colors.warning};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.warning};
  font-size: 0.8rem;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const ActionRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const PaginationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.md};
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const JsonBlock = styled.pre`
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  font-size: 0.8rem;
  max-height: 300px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const DeliveryStatusBadge = styled.span`
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${({ $variant }) => {
    switch ($variant) {
      case 'Accepted': return '#28a74520';
      case 'Rejected': return '#dc354520';
      case 'Error': return '#dc354520';
      case 'Duplicate': return '#ffc10720';
      default: return '#6c757d20';
    }
  }};
  color: ${({ $variant }) => {
    switch ($variant) {
      case 'Accepted': return '#28a745';
      case 'Rejected': return '#dc3545';
      case 'Error': return '#dc3545';
      case 'Duplicate': return '#ffc107';
      default: return '#6c757d';
    }
  }};
`;

const TokenDisplay = styled.code`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  letter-spacing: 0.5px;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  font-size: 0.8rem;
  text-decoration: underline;
  padding: 0;
  &:hover { opacity: 0.8; }
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${({ $type, theme }) =>
    $type === 'outbound' ? `${theme.colors.primary}15` : '#28a74515'};
  color: ${({ $type, theme }) =>
    $type === 'outbound' ? theme.colors.primary : '#28a745'};
`;

const FilterRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  align-items: flex-end;
  flex-wrap: wrap;
`;

// ─── Helpers ────────────────────────────────────────────────────

const formatDate = (d) => d ? new Date(d).toLocaleString() : '-';

const deliveryStatusColor = (status) => {
  switch (status) {
    case 0: return undefined; // Pending
    case 2: return true;      // Completed (green)
    case 1: return undefined; // Processing
    case 3: return false;     // Failed (red)
    default: return undefined;
  }
};

const deliveryStatusLabel = (status) => {
  switch (status) {
    case 0: return 'Pending';
    case 1: return 'Processing';
    case 2: return 'Completed';
    case 3: return 'Failed';
    default: return String(status);
  }
};

const maskToken = (token) => {
  if (!token) return '-';
  if (token.length <= 12) return token;
  return `${token.slice(0, 8)}...${token.slice(-4)}`;
};

// ─── Component ──────────────────────────────────────────────────

export const WebhooksSection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Internal sub-tab lives in `?sub=` (the top-level `?tab=` belongs to the
  // Settings page that hosts this section). Use the functional updater so
  // `tab=integrations` is preserved when switching sub-tabs.
  const tab = searchParams.get('sub') || 'outbound';
  const setTab = (t) => setSearchParams((prev) => { prev.set('sub', t); return prev; });

  // Outbound state
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(false);

  // Inbound state
  const [inboundEndpoints, setInboundEndpoints] = useState([]);

  // Delivery log state
  const [deliveries, setDeliveries] = useState([]);
  const [totalDeliveries, setTotalDeliveries] = useState(0);
  const [deliveryPage, setDeliveryPage] = useState(1);
  const [filterEndpointId, setFilterEndpointId] = useState('');
  const [deliveryType, setDeliveryType] = useState('all');

  // Secret display
  const [shownSecret, setShownSecret] = useState(null);

  // Delivery detail
  const [deliveryDetail, setDeliveryDetail] = useState(null);

  // Confirm dialog
  const [confirmAction, setConfirmAction] = useState(null);

  // ─── Data Loading ─────────────────────────────────────────────

  const loadEndpoints = useCallback(async () => {
    try {
      const data = await webhookService.getEndpoints();
      setEndpoints(data || []);
    } catch (err) {
      toast.error('Failed to load outbound webhook endpoints');
    }
  }, []);

  const loadInboundEndpoints = useCallback(async () => {
    try {
      const data = await inboundWebhookService.getEndpoints();
      setInboundEndpoints(data || []);
    } catch (err) {
      toast.error('Failed to load inbound webhook endpoints');
    }
  }, []);

  const loadDeliveries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: deliveryPage, pageSize: 20 };
      if (filterEndpointId) params.endpointId = filterEndpointId;

      if (deliveryType === 'outbound') {
        const data = await webhookService.getDeliveries(params);
        const items = (data?.items || []).map(d => ({ ...d, _type: 'outbound' }));
        setDeliveries(items);
        setTotalDeliveries(data?.totalCount || 0);
      } else if (deliveryType === 'inbound') {
        const data = await inboundWebhookService.getDeliveries(params);
        const items = (data?.items || []).map(d => ({ ...d, _type: 'inbound' }));
        setDeliveries(items);
        setTotalDeliveries(data?.totalCount || 0);
      } else {
        // 'all' — fetch both in parallel, combine and sort by date descending
        const [outData, inData] = await Promise.all([
          webhookService.getDeliveries(filterEndpointId ? params : { page: 1, pageSize: 100 }),
          inboundWebhookService.getDeliveries(filterEndpointId ? params : { page: 1, pageSize: 100 }),
        ]);
        const outItems = (outData?.items || []).map(d => ({ ...d, _type: 'outbound' }));
        const inItems = (inData?.items || []).map(d => ({ ...d, _type: 'inbound' }));
        const combined = [...outItems, ...inItems].sort((a, b) => {
          const dateA = new Date(a.createdAt || a.receivedAt || 0);
          const dateB = new Date(b.createdAt || b.receivedAt || 0);
          return dateB - dateA;
        });
        setDeliveries(combined);
        setTotalDeliveries((outData?.totalCount || 0) + (inData?.totalCount || 0));
      }
    } catch (err) {
      toast.error('Failed to load delivery logs');
    }
    setLoading(false);
  }, [deliveryPage, filterEndpointId, deliveryType]);

  useEffect(() => {
    loadEndpoints();
    loadInboundEndpoints();
  }, [loadEndpoints, loadInboundEndpoints]);

  useEffect(() => {
    if (tab === 'deliveries') loadDeliveries();
  }, [tab, loadDeliveries]);

  // ─── Outbound Endpoint Actions ────────────────────────────────

  const handleDeleteOutbound = async (id) => {
    setConfirmAction({
      message: 'Delete this webhook endpoint? All delivery history will be lost.',
      onConfirm: async () => {
        try {
          await webhookService.deleteEndpoint(id);
          toast.success('Endpoint deleted');
          loadEndpoints();
        } catch { toast.error('Failed to delete'); }
        setConfirmAction(null);
      },
    });
  };

  const handleTest = async (id) => {
    try {
      await webhookService.testEndpoint(id);
      toast.success('Test ping sent');
      loadEndpoints();
    } catch { toast.error('Test ping failed'); }
  };

  const handleRegenerateSecret = (id) => {
    setConfirmAction({
      message: 'Regenerate secret? The old secret will stop working immediately.',
      onConfirm: async () => {
        try {
          const result = await webhookService.regenerateSecret(id);
          if (result?.secret) setShownSecret(result.secret);
          toast.success('Secret regenerated');
        } catch { toast.error('Failed to regenerate secret'); }
        setConfirmAction(null);
      },
    });
  };

  const handleEnable = async (id) => {
    try {
      await webhookService.enableEndpoint(id);
      toast.success('Endpoint re-enabled');
      loadEndpoints();
    } catch { toast.error('Failed to enable'); }
  };

  const handleViewDelivery = async (id) => {
    try {
      const detail = await webhookService.getDeliveryDetail(id);
      setDeliveryDetail(detail);
    } catch { toast.error('Failed to load delivery detail'); }
  };

  // ─── Inbound Endpoint Actions ─────────────────────────────────

  const handleDeleteInbound = (id) => {
    setConfirmAction({
      message: 'Delete this inbound webhook endpoint? All delivery history will be lost.',
      onConfirm: async () => {
        try {
          await inboundWebhookService.deleteEndpoint(id);
          toast.success('Endpoint deleted');
          loadInboundEndpoints();
        } catch { toast.error('Failed to delete'); }
        setConfirmAction(null);
      },
    });
  };

  // ─── Computed ─────────────────────────────────────────────────

  const pageSize = 20;
  const totalPages = Math.ceil(totalDeliveries / pageSize);

  // Endpoint options for delivery log filter — depends on deliveryType
  const endpointFilterOptions = (() => {
    if (deliveryType === 'outbound') {
      return endpoints.map(ep => ({ id: ep.id, name: ep.name }));
    }
    if (deliveryType === 'inbound') {
      return inboundEndpoints.map(ep => ({ id: ep.id, name: ep.name }));
    }
    // 'all' — combine with type labels
    return [
      ...endpoints.map(ep => ({ id: ep.id, name: `[Outbound] ${ep.name}` })),
      ...inboundEndpoints.map(ep => ({ id: ep.id, name: `[Inbound] ${ep.name}` })),
    ];
  })();

  // ─── Render ─────────────────────────────────────────────────

  return (
    <PageContainer theme={theme}>
      <PageHeaderRow theme={theme}>
        <div>
          <ThemedText type="title">Webhooks</ThemedText>
          <p style={{ color: theme.colors.muted, fontSize: '0.875rem', margin: '6px 0 0' }}>
            Manage outbound and inbound webhook integrations
          </p>
        </div>
        {tab === 'outbound' && (
          <Button onClick={() => navigate('/webhooks/outbound/new')}>
            <Plus size={14} /> New Outbound Endpoint
          </Button>
        )}
        {tab === 'inbound' && (
          <Button onClick={() => navigate('/webhooks/inbound/new')}>
            <Plus size={14} /> New Inbound Endpoint
          </Button>
        )}
      </PageHeaderRow>

      <TabRow theme={theme}>
        <Tab theme={theme} $active={tab === 'outbound'} onClick={() => setTab('outbound')}>
          Outbound ({endpoints.length})
        </Tab>
        <Tab theme={theme} $active={tab === 'inbound'} onClick={() => setTab('inbound')}>
          Inbound ({inboundEndpoints.length})
        </Tab>
        <Tab theme={theme} $active={tab === 'deliveries'} onClick={() => setTab('deliveries')}>
          Delivery Log
        </Tab>
      </TabRow>

      {/* ─── Outbound Tab ─── */}
      {tab === 'outbound' && (
        <TableContainer theme={theme}>
          <Table theme={theme}>
            <Thead theme={theme}>
              <tr>
                <Th theme={theme}>Name</Th>
                <Th theme={theme}>URL</Th>
                <Th theme={theme}>Status</Th>
                <Th theme={theme}>Events</Th>
                <Th theme={theme}>Failures</Th>
                <Th theme={theme}>Actions</Th>
              </tr>
            </Thead>
            <Tbody>
              {endpoints.length === 0 && (
                <Tr theme={theme}>
                  <Td theme={theme} colSpan={6}>
                    <EmptyState theme={theme}>
                      {loading ? 'Loading...' : 'No outbound webhook endpoints configured. Click "New Outbound Endpoint" to get started.'}
                    </EmptyState>
                  </Td>
                </Tr>
              )}
              {endpoints.map(ep => (
                <Tr key={ep.id} theme={theme}>
                  <Td theme={theme}>
                    {ep.disabledAt && (
                      <DisabledBar theme={theme}>
                        Auto-disabled: {ep.disabledReason}
                        <Button variant="secondary" size="sm" onClick={() => handleEnable(ep.id)} style={{ marginLeft: 8 }}>
                          Re-enable
                        </Button>
                      </DisabledBar>
                    )}
                    <strong>{ep.name}</strong>
                    {ep.description && <div style={{ fontSize: '0.8rem', color: theme.colors.textSecondary }}>{ep.description}</div>}
                  </Td>
                  <Td theme={theme} style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ep.url}
                  </Td>
                  <Td theme={theme}>
                    <StatusBadge isActive={ep.isActive}>{ep.isActive ? 'Active' : 'Inactive'}</StatusBadge>
                  </Td>
                  <Td theme={theme}>
                    {(ep.subscribedEvents || []).map(e => <EventBadge key={e} theme={theme}>{e}</EventBadge>)}
                  </Td>
                  <Td theme={theme}>{ep.consecutiveFailures || 0}</Td>
                  <Td theme={theme}>
                    <ActionRow theme={theme}>
                      <Button variant="secondary" size="sm" onClick={() => navigate(`/webhooks/outbound/${ep.id}/edit`)}>Edit</Button>
                      <Button variant="secondary" size="sm" onClick={() => handleTest(ep.id)}>Test</Button>
                      <Button variant="secondary" size="sm" onClick={() => handleRegenerateSecret(ep.id)}>Secret</Button>
                      <Button variant="danger" size="sm" onClick={() => handleDeleteOutbound(ep.id)}>Delete</Button>
                    </ActionRow>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {/* ─── Inbound Tab ─── */}
      {tab === 'inbound' && (
        <TableContainer theme={theme}>
          <Table theme={theme}>
            <Thead theme={theme}>
              <tr>
                <Th theme={theme}>Name</Th>
                <Th theme={theme}>Form</Th>
                <Th theme={theme}>Token</Th>
                <Th theme={theme}>Rate Limit</Th>
                <Th theme={theme}>Status</Th>
                <Th theme={theme}>Created</Th>
                <Th theme={theme}>Actions</Th>
              </tr>
            </Thead>
            <Tbody>
              {inboundEndpoints.length === 0 && (
                <Tr theme={theme}>
                  <Td theme={theme} colSpan={7}>
                    <EmptyState theme={theme}>
                      {loading ? 'Loading...' : 'No inbound webhook endpoints configured. Click "New Inbound Endpoint" to get started.'}
                    </EmptyState>
                  </Td>
                </Tr>
              )}
              {inboundEndpoints.map(ep => (
                <Tr key={ep.id} theme={theme}>
                  <Td theme={theme}>
                    <strong>{ep.name}</strong>
                    {ep.description && (
                      <div style={{ fontSize: '0.8rem', color: theme.colors.textSecondary }}>
                        {ep.description}
                      </div>
                    )}
                  </Td>
                  <Td theme={theme}>{ep.formSchemaName || ep.formSchemaId || '-'}</Td>
                  <Td theme={theme}>
                    <TokenDisplay theme={theme}>{maskToken(ep.token)}</TokenDisplay>
                  </Td>
                  <Td theme={theme}>
                    {ep.rateLimitPerMinute ? `${ep.rateLimitPerMinute}/min` : '-'}
                  </Td>
                  <Td theme={theme}>
                    <StatusBadge isActive={ep.isActive}>
                      {ep.isActive ? 'Active' : 'Inactive'}
                    </StatusBadge>
                  </Td>
                  <Td theme={theme} style={{ fontSize: '0.8rem' }}>
                    {formatDate(ep.createdAt)}
                  </Td>
                  <Td theme={theme}>
                    <ActionRow theme={theme}>
                      <Button variant="secondary" size="sm" onClick={() => navigate(`/webhooks/inbound/${ep.id}/edit`)}>
                        Edit
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => handleDeleteInbound(ep.id)}>
                        Delete
                      </Button>
                    </ActionRow>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {/* ─── Combined Delivery Log Tab ─── */}
      {tab === 'deliveries' && (
        <>
          <FilterRow theme={theme}>
            <FormGroup theme={theme} style={{ maxWidth: 200, margin: 0 }}>
              <FormLabel theme={theme}>Type</FormLabel>
              <StyledSelect
                theme={theme}
                value={deliveryType}
                onChange={e => {
                  setDeliveryType(e.target.value);
                  setFilterEndpointId('');
                  setDeliveryPage(1);
                }}
              >
                <option value="all">All Types</option>
                <option value="outbound">Outbound</option>
                <option value="inbound">Inbound</option>
              </StyledSelect>
            </FormGroup>
            <FormGroup theme={theme} style={{ maxWidth: 300, margin: 0 }}>
              <FormLabel theme={theme}>Filter by Endpoint</FormLabel>
              <StyledSelect
                theme={theme}
                value={filterEndpointId}
                onChange={e => { setFilterEndpointId(e.target.value); setDeliveryPage(1); }}
              >
                <option value="">All Endpoints</option>
                {endpointFilterOptions.map(ep => (
                  <option key={ep.id} value={ep.id}>{ep.name}</option>
                ))}
              </StyledSelect>
            </FormGroup>
          </FilterRow>

          <TableContainer theme={theme}>
            <Table theme={theme}>
              <Thead theme={theme}>
                <tr>
                  {/* Columns vary by deliveryType */}
                  {deliveryType === 'outbound' && (
                    <>
                      <Th theme={theme}>Event</Th>
                      <Th theme={theme}>Endpoint</Th>
                      <Th theme={theme}>Status</Th>
                      <Th theme={theme}>HTTP</Th>
                      <Th theme={theme}>Duration</Th>
                      <Th theme={theme}>Retries</Th>
                      <Th theme={theme}>Time</Th>
                      <Th theme={theme}>Actions</Th>
                    </>
                  )}
                  {deliveryType === 'inbound' && (
                    <>
                      <Th theme={theme}>Time</Th>
                      <Th theme={theme}>Endpoint</Th>
                      <Th theme={theme}>Status</Th>
                      <Th theme={theme}>Source IP</Th>
                      <Th theme={theme}>Submission</Th>
                      <Th theme={theme}>Duration</Th>
                    </>
                  )}
                  {deliveryType === 'all' && (
                    <>
                      <Th theme={theme}>Type</Th>
                      <Th theme={theme}>Event / Time</Th>
                      <Th theme={theme}>Endpoint</Th>
                      <Th theme={theme}>Status</Th>
                      <Th theme={theme}>Duration</Th>
                      <Th theme={theme}>Actions</Th>
                    </>
                  )}
                </tr>
              </Thead>
              <Tbody>
                {deliveries.length === 0 && (
                  <Tr theme={theme}>
                    <Td theme={theme} colSpan={deliveryType === 'outbound' ? 8 : deliveryType === 'inbound' ? 6 : 6}>
                      <EmptyState theme={theme}>
                        {loading ? 'Loading...' : 'No delivery logs found.'}
                      </EmptyState>
                    </Td>
                  </Tr>
                )}

                {/* Outbound-only rows */}
                {deliveryType === 'outbound' && deliveries.map(d => (
                  <Tr key={d.id} theme={theme}>
                    <Td theme={theme}><EventBadge theme={theme}>{d.eventType}</EventBadge></Td>
                    <Td theme={theme}>{d.endpointName}</Td>
                    <Td theme={theme}>
                      <StatusBadge isActive={deliveryStatusColor(d.status)}>
                        {deliveryStatusLabel(d.status)}
                      </StatusBadge>
                    </Td>
                    <Td theme={theme}>{d.responseStatusCode || '-'}</Td>
                    <Td theme={theme}>{d.durationMs != null ? `${d.durationMs}ms` : '-'}</Td>
                    <Td theme={theme}>{d.retryCount}/{d.maxRetries}</Td>
                    <Td theme={theme} style={{ fontSize: '0.8rem' }}>{formatDate(d.createdAt)}</Td>
                    <Td theme={theme}>
                      <Button variant="secondary" size="sm" onClick={() => handleViewDelivery(d.id)}>Detail</Button>
                    </Td>
                  </Tr>
                ))}

                {/* Inbound-only rows */}
                {deliveryType === 'inbound' && deliveries.map(d => (
                  <Tr key={d.id} theme={theme}>
                    <Td theme={theme} style={{ fontSize: '0.8rem' }}>
                      {formatDate(d.receivedAt || d.createdAt)}
                    </Td>
                    <Td theme={theme}>{d.endpointName || '-'}</Td>
                    <Td theme={theme}>
                      <DeliveryStatusBadge theme={theme} $variant={d.status}>
                        {d.status || '-'}
                      </DeliveryStatusBadge>
                    </Td>
                    <Td theme={theme} style={{ fontSize: '0.8rem' }}>
                      {d.sourceIp || '-'}
                    </Td>
                    <Td theme={theme}>
                      {d.submissionId && d.formSchemaId ? (
                        <LinkButton
                          theme={theme}
                          onClick={() => navigate(`/forms/${d.formSchemaId}/submissions/${d.submissionId}`)}
                        >
                          View
                        </LinkButton>
                      ) : (
                        '-'
                      )}
                    </Td>
                    <Td theme={theme}>
                      {d.durationMs != null ? `${d.durationMs}ms` : '-'}
                    </Td>
                  </Tr>
                ))}

                {/* Combined "All" rows */}
                {deliveryType === 'all' && deliveries.map(d => (
                  <Tr key={`${d._type}-${d.id}`} theme={theme}>
                    <Td theme={theme}>
                      <TypeBadge theme={theme} $type={d._type}>
                        {d._type}
                      </TypeBadge>
                    </Td>
                    <Td theme={theme} style={{ fontSize: '0.8rem' }}>
                      {d._type === 'outbound'
                        ? <EventBadge theme={theme}>{d.eventType}</EventBadge>
                        : formatDate(d.receivedAt || d.createdAt)
                      }
                    </Td>
                    <Td theme={theme}>{d.endpointName || '-'}</Td>
                    <Td theme={theme}>
                      {d._type === 'outbound' ? (
                        <StatusBadge isActive={deliveryStatusColor(d.status)}>
                          {deliveryStatusLabel(d.status)}
                        </StatusBadge>
                      ) : (
                        <DeliveryStatusBadge theme={theme} $variant={d.status}>
                          {d.status || '-'}
                        </DeliveryStatusBadge>
                      )}
                    </Td>
                    <Td theme={theme}>
                      {d.durationMs != null ? `${d.durationMs}ms` : '-'}
                    </Td>
                    <Td theme={theme}>
                      {d._type === 'outbound' ? (
                        <Button variant="secondary" size="sm" onClick={() => handleViewDelivery(d.id)}>Detail</Button>
                      ) : (
                        d.submissionId && d.formSchemaId ? (
                          <LinkButton
                            theme={theme}
                            onClick={() => navigate(`/forms/${d.formSchemaId}/submissions/${d.submissionId}`)}
                          >
                            View
                          </LinkButton>
                        ) : '-'
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <PaginationRow theme={theme}>
              <span>Page {deliveryPage} of {totalPages} ({totalDeliveries} total)</span>
              <div>
                <Button variant="secondary" size="sm" disabled={deliveryPage <= 1} onClick={() => setDeliveryPage(p => p - 1)}>
                  Previous
                </Button>
                {' '}
                <Button variant="secondary" size="sm" disabled={deliveryPage >= totalPages} onClick={() => setDeliveryPage(p => p + 1)}>
                  Next
                </Button>
              </div>
            </PaginationRow>
          )}
        </>
      )}

      {/* ─── Secret Display Modal ─── */}
      {shownSecret && (
        <ModalOverlay $isOpen theme={theme}>
          <ModalContent theme={theme} style={{ maxWidth: 500 }}>
            <ModalHeader theme={theme}>
              <h3>Webhook Secret</h3>
            </ModalHeader>
            <ModalBody theme={theme}>
              <p style={{ color: theme.colors.warning, fontWeight: 500, marginBottom: theme.spacing.sm }}>
                Copy this secret now. It will not be shown again.
              </p>
              <SecretDisplay theme={theme}>
                <SecretCode>{shownSecret}</SecretCode>
                <Button variant="secondary" size="sm" onClick={() => { navigator.clipboard.writeText(shownSecret); toast.success('Copied!'); }}>
                  Copy
                </Button>
              </SecretDisplay>
              <p style={{ fontSize: '0.8rem', color: theme.colors.textSecondary, marginTop: theme.spacing.sm }}>
                Use this secret to verify webhook signatures using HMAC-SHA256.
                The signature is sent in the <code>X-Webhook-Signature</code> header as <code>sha256=&lt;hex&gt;</code>.
              </p>
            </ModalBody>
            <ModalFooter theme={theme}>
              <Button onClick={() => setShownSecret(null)}>Done</Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* ─── Delivery Detail Modal ─── */}
      {deliveryDetail && (
        <ModalOverlay $isOpen theme={theme}>
          <ModalContent theme={theme} style={{ maxWidth: 650 }}>
            <ModalHeader theme={theme}>
              <h3>Delivery Detail #{deliveryDetail.id}</h3>
            </ModalHeader>
            <ModalBody theme={theme}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.sm, marginBottom: theme.spacing.md }}>
                <div><strong>Event:</strong> {deliveryDetail.eventType}</div>
                <div><strong>Status:</strong> {deliveryStatusLabel(deliveryDetail.status)}</div>
                <div><strong>Endpoint:</strong> {deliveryDetail.endpointName}</div>
                <div><strong>HTTP Code:</strong> {deliveryDetail.responseStatusCode || '-'}</div>
                <div><strong>Duration:</strong> {deliveryDetail.durationMs != null ? `${deliveryDetail.durationMs}ms` : '-'}</div>
                <div><strong>Retries:</strong> {deliveryDetail.retryCount}/{deliveryDetail.maxRetries}</div>
                <div><strong>Created:</strong> {formatDate(deliveryDetail.createdAt)}</div>
                <div><strong>Attempted:</strong> {formatDate(deliveryDetail.attemptedAt)}</div>
              </div>
              {deliveryDetail.endpointUrl && (
                <div style={{ marginBottom: theme.spacing.sm }}>
                  <strong>URL:</strong> <span style={{ fontSize: '0.85rem' }}>{deliveryDetail.endpointUrl}</span>
                </div>
              )}
              {deliveryDetail.errorMessage && (
                <div style={{ marginBottom: theme.spacing.sm, color: theme.colors.danger }}>
                  <strong>Error:</strong> {deliveryDetail.errorMessage}
                </div>
              )}
              <FormLabel theme={theme}>Payload</FormLabel>
              <JsonBlock theme={theme}>
                {(() => {
                  try { return JSON.stringify(JSON.parse(deliveryDetail.payloadJson), null, 2); }
                  catch { return deliveryDetail.payloadJson; }
                })()}
              </JsonBlock>
              {deliveryDetail.responseBody && (
                <>
                  <FormLabel theme={theme} style={{ marginTop: theme.spacing.sm }}>Response Body</FormLabel>
                  <JsonBlock theme={theme}>{deliveryDetail.responseBody}</JsonBlock>
                </>
              )}
            </ModalBody>
            <ModalFooter theme={theme}>
              <Button onClick={() => setDeliveryDetail(null)}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* ─── Confirm Dialog ─── */}
      {confirmAction && (
        <ModalOverlay $isOpen theme={theme}>
          <ModalContent theme={theme} style={{ maxWidth: 400 }}>
            <ModalHeader theme={theme}><h3>Confirm</h3></ModalHeader>
            <ModalBody theme={theme}><p>{confirmAction.message}</p></ModalBody>
            <ModalFooter theme={theme}>
              <Button variant="secondary" onClick={() => setConfirmAction(null)}>Cancel</Button>
              <Button variant="danger" onClick={confirmAction.onConfirm}>
                Confirm
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export const WebhooksPage = () => (
  <Layout currentPath="/settings">
    <WebhooksSection />
  </Layout>
);

export default WebhooksPage;
